import { useEffect, useMemo, useState, Suspense, lazy, type ComponentType } from "react"
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"

import { Toaster } from "@/components/ui/toaster"
import { Toaster as Sonner } from "@/components/ui/sonner"
import { TooltipProvider } from "@/components/ui/tooltip"
import { AuthProvider, useAuth } from "@/hooks/useAuth"
import { SessionTimeoutProvider } from "@/components/SessionTimeoutProvider"
import { ScreenReaderAnnounceProvider } from "@/hooks/useScreenReaderAnnounce"
import { useRTL } from "@/hooks/useRTL"
import { useWebViewResilience } from "@/hooks/useWebViewResilience"
import ErrorBoundary from "@/components/ErrorBoundary"
import { Capacitor } from "@capacitor/core"
const Settings = lazy(() => import("./pages/Settings"))
const SettingsLite = lazy(() => import("./pages/SettingsLite"))
console.log("APP_MARKER_2026_01_14_B")

// SECTION: LAZY_COMPONENTS
const OfflineIndicator = lazy(() => import("@/components/OfflineIndicator"))
const PWAInstallPrompt = lazy(() => import("@/components/PWAInstallPrompt"))

const SyncStatus = lazy(() => import("@/components/SyncStatus"))
const SplashScreen = lazy(() => import("@/components/SplashScreen"))
const WhatsNewModal = lazy(() => import("@/components/WhatsNewModal"))
const KeyboardShortcutsHelp = lazy(() => import("@/components/KeyboardShortcutsHelp"))
const KeyboardNavigation = lazy(() => import("@/components/KeyboardNavigation"))

const Index = lazy(() => import("./pages/Index"))
const Dashboard = lazy(() => import("./pages/Dashboard"))
const NotFound = lazy(() => import("./pages/NotFound"))
const Auth = lazy(() => import("./pages/Auth"))
const AuthCallback = lazy(() => import("./pages/AuthCallback"))
const Onboarding = lazy(() => import("./pages/Onboarding"))


const SalesTrainer = lazy(async () => {
  const mod = await import("./components/sales-trainer/SalesTrainer")
  const Comp: any = (mod as any).default ?? (mod as any).SalesTrainer
  return { default: Comp }
}) as unknown as ComponentType<{ onClose: () => void }>

const Admin = lazy(() => import("./pages/Admin"))
const Offline = lazy(() => import("./pages/Offline"))
const Install = lazy(() => import("./pages/Install"))
const Achievements = lazy(() => import("./pages/Achievements"))
const Subscription = lazy(() => import("./pages/Subscription"))
const About = lazy(() => import("./pages/About"))
const Careers = lazy(() => import("./pages/Careers"))
const Contact = lazy(() => import("./pages/Contact"))
const Privacy = lazy(() => import("./pages/Privacy"))
const Terms = lazy(() => import("./pages/Terms"))
const Cookies = lazy(() => import("./pages/Cookies"))
const Blog = lazy(() => import("./pages/Blog"))
const BlogPost = lazy(() => import("./pages/BlogPost"))
const BlogCategory = lazy(() => import("./pages/BlogCategory"))
const BlogTag = lazy(() => import("./pages/BlogTag"))
const Methodology = lazy(() => import("./pages/Methodology"))
const PerformanceChecklist = lazy(() => import("./pages/PerformanceChecklist"))
const Status = lazy(() => import("./pages/Status"))

// SECTION: FALLBACKS
const PageLoader = () => <div className="min-h-screen bg-background" />
const DeferredNull = () => null

// SECTION: STANDALONE_DETECT
const isStandalone = () => {
  if (typeof window === "undefined") return false
  try {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any)?.standalone === true
    )
  } catch {
    return false
  }
}

// SECTION: AUTH_GUARD
function RequireAuth({ children }: { children: JSX.Element }) {
  const location = useLocation()
  const auth: any = useAuth?.() as any

  const loading =
    auth?.loading ??
    auth?.isLoading ??
    auth?.isInitializing ??
    auth?.initializing ??
    false

  const isAuthed =
    Boolean(auth?.session) || Boolean(auth?.user) || Boolean(auth?.isAuthenticated)

  if (loading) return <PageLoader />

  if (!isAuthed) {
    return <Navigate to="/auth" replace state={{ from: location.pathname }} />
  }

  return children
}

// SECTION: DASHBOARD_SHELL_NAV
function DashboardShell({ children }: { children: JSX.Element }) {
  const navigate = useNavigate()
  const location = useLocation()
  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/")

  return (
    <div className="min-h-screen bg-background">
      <div
        className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-2 gap-2">
          <div className="flex items-center gap-1 flex-wrap">
            <button
              type="button"
              className={`rounded-md border px-3 py-2 text-sm transition-colors ${
                isActive("/dashboard")
                  ? "bg-primary text-primary-foreground border-primary"
                  : "hover:bg-muted"
              }`}
              onClick={() => navigate("/dashboard", { replace: false })}
            >
              Dashboard
            </button>
            <button
              type="button"
              className={`rounded-md border px-3 py-2 text-sm transition-colors ${
                isActive("/achievements")
                  ? "bg-primary text-primary-foreground border-primary"
                  : "hover:bg-muted"
              }`}
              onClick={() => navigate("/achievements", { replace: false })}
            >
              Achievements
            </button>
            <button
              type="button"
              className="rounded-md border px-3 py-2 text-sm font-medium hover:bg-muted transition-colors"
              onClick={() => navigate("/subscription", { replace: false })}
            >
              Upgrade
            </button>
          </div>

          <button
            type="button"
            className={`rounded-md border px-3 py-2 text-sm transition-colors ${
              isActive("/settings")
                ? "bg-primary text-primary-foreground border-primary"
                : "hover:bg-muted"
            }`}
            onClick={() => navigate("/settings", { replace: false })}
          >
            Settings
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-0">{children}</div>
    </div>
  )
}
// SECTION: SAFE_SETTINGS_PLACEHOLDER
function SafeSettingsPlaceholder() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-background p-4">
      <h1 className="text-xl font-semibold">Settings (Safe Mode)</h1>
      <p className="mt-2 text-sm opacity-80">
        If you can see this, the real Settings page is what’s crashing the WebView.
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          className="rounded-md border px-3 py-2 text-sm"
          onClick={() => navigate("/dashboard")}
        >
          Back to Dashboard
        </button>
        <button
          type="button"
          className="rounded-md border px-3 py-2 text-sm"
          onClick={() => navigate("/")}
        >
          Home
        </button>
      </div>
    </div>
  )
}

export default function App() {
  const queryClient = useMemo(() => new QueryClient(), [])

  const [showSplash, setShowSplash] = useState(() => {
    if (typeof window === "undefined") return false
    const hasShownSplash = sessionStorage.getItem("splash-shown")
    return isStandalone() && !hasShownSplash
  })

  useRTL()
  useWebViewResilience()

  // SECTION: BOOT_MARKERS
  useEffect(() => {
    if (typeof window === "undefined") return
    console.log("APP_MARKER_2026_01_14_B")
  }, [])

  // SECTION: SPLASH_STATE
  useEffect(() => {
    if (typeof window === "undefined") return
    if (showSplash) sessionStorage.setItem("splash-shown", "true")
  }, [showSplash])

  // SECTION: ACCESSIBILITY_PREFS
  useEffect(() => {
    if (typeof window === "undefined") return

    const savedPref = localStorage.getItem("reduceMotionEnabled")
    const systemPref = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    const shouldReduce = savedPref !== null ? savedPref === "true" : systemPref
    if (shouldReduce) document.documentElement.classList.add("reduce-motion")

    const savedContrastPref = localStorage.getItem("highContrastEnabled")
    const systemContrastPref = window.matchMedia("(prefers-contrast: more)").matches
    const shouldHighContrast =
      savedContrastPref !== null ? savedContrastPref === "true" : systemContrastPref
    if (shouldHighContrast) document.documentElement.classList.add("high-contrast")

    const savedFontSize = localStorage.getItem("fontSizeScale")
    if (savedFontSize) document.documentElement.style.fontSize = `${savedFontSize}%`

    const savedLineHeight = localStorage.getItem("lineHeightScale")
    if (savedLineHeight) {
      document.documentElement.style.setProperty("--line-height-base", savedLineHeight)
      document.body.style.lineHeight = savedLineHeight
    }

    const savedDyslexicFont = localStorage.getItem("dyslexicFontEnabled")
    if (savedDyslexicFont === "true") {
      document.documentElement.classList.add("dyslexic-font")
    }

    const savedFocusIndicator = localStorage.getItem("focusIndicatorEnabled")
    if (savedFocusIndicator === "true") {
      document.documentElement.classList.add("enhanced-focus")
    }
  }, [])

  // SECTION: PROTECTED_WRAPPER
  const Protected = ({ children }: { children: JSX.Element }) => (
    <RequireAuth>{children}</RequireAuth>
  )

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SessionTimeoutProvider>
            <ScreenReaderAnnounceProvider>
              <TooltipProvider>
                <Suspense fallback={<DeferredNull />}>
                  {showSplash ? <SplashScreen onComplete={() => setShowSplash(false)} /> : null}
                  <OfflineIndicator />
                  
                  <SyncStatus />
                  <KeyboardShortcutsHelp />
                </Suspense>

                <Toaster />
                <Sonner />

                <Suspense fallback={<DeferredNull />}>
                  <KeyboardNavigation />
                  <PWAInstallPrompt />
                  <WhatsNewModal />
                </Suspense>

                <main id="main-content" className="safe-area">
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
  <Route path="/" element={<Index />} />
  <Route path="/auth" element={<Auth />} />
  <Route path="/auth/callback" element={<AuthCallback />} />
  <Route path="/onboarding" element={<Onboarding />} />

  // Dashboard
<Route
  path="/dashboard"
  element={
    <Protected>
      <DashboardShell>
        <Dashboard />
      </DashboardShell>
    </Protected>
  }
/>


<Route path="/app" element={<Navigate to="/dashboard" replace />} />

// Settings
<Route
  path="/settings"
  element={
    <Protected>
      <DashboardShell>
        {Capacitor.isNativePlatform() ? <SettingsLite /> : <Settings />}
      </DashboardShell>
    </Protected>
  }
/>

<Route
  path="/settings-safe"
  element={
    <Protected>
      <DashboardShell>
        <SafeSettingsPlaceholder />
      </DashboardShell>
    </Protected>
  }
/>

  <Route path="/paywall-test" element={<Navigate to="/subscription" replace />} />

  <Route
    path="/admin"
    element={
      <Protected>
        <Admin />
      </Protected>
    }
  />

  <Route
    path="/achievements"
    element={
      <Protected>
        <DashboardShell>
          <Achievements />
        </DashboardShell>
      </Protected>
    }
  />

  <Route
    path="/subscription"
    element={
      <Protected>
        <Subscription />
      </Protected>
    }
  />

  <Route path="/offline" element={<Offline />} />
  <Route path="/install" element={<Install />} />
  <Route path="/about" element={<About />} />
  <Route path="/careers" element={<Careers />} />
  <Route path="/contact" element={<Contact />} />
  <Route path="/privacy" element={<Privacy />} />
  <Route path="/terms" element={<Terms />} />
  <Route path="/cookies" element={<Cookies />} />
  <Route path="/blog" element={<Blog />} />
  <Route path="/blog/category/:category" element={<BlogCategory />} />
  <Route path="/blog/tag/:tag" element={<BlogTag />} />
  <Route path="/blog/:slug" element={<BlogPost />} />
  <Route path="/methodology" element={<Methodology />} />
  <Route path="/performance-checklist" element={<PerformanceChecklist />} />
  <Route path="/status" element={<Status />} />

  <Route path="*" element={<NotFound />} />
</Routes>
                  </Suspense>
                </main>
              </TooltipProvider>
            </ScreenReaderAnnounceProvider>
          </SessionTimeoutProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}